/* eslint-disable no-underscore-dangle */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Button, Card, Form, Modal, OverlayTrigger, Spinner, Table, Tooltip,
} from 'react-bootstrap';
import dayjs from 'dayjs';
import Error from '../../../Components/Error';
import PaymentsService from '../../../Services/PaymentsService';
import counterpartyIdOf from './counterpartyIdOf';
import PartnersService from '../../../Services/PartnersService';
import OperatorService from '../../../Services/OperatorService';
import { formatDate, formatDateTime } from '../../../Utils/timezone';
import PayerBillingDetails from './PayerBillingDetails';
import PaymentAllocationRows, {
  newAllocationRow,
  formatRowAmount,
  validateAllocationRows,
  rowsToLinesPayload,
} from './PaymentAllocationRows';

// HUF gets rounded + ' Ft', everything else gets the EUR symbol + 2dp.
function formatMoney(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const n = Number(amount);
  if (currency === 'HUF') return `${Math.round(n).toLocaleString('en-US')} Ft`;
  return `€${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Truncated 2-decimal rounding — same float-trap guard BulkPaymentModal
// uses for allocation sums.
function round2(n) {
  return Math.round(n * 100) / 100;
}

function startOfMonthYmd() {
  return dayjs().startOf('month').format('YYYY-MM-DD');
}
function endOfMonthYmd() {
  return dayjs().endOf('month').format('YYYY-MM-DD');
}

// Mode constants — keep stringly-typed switching contained.
const MODE_MANUAL = 'manual';
const MODE_AUTO = 'auto';

function LinkToBookingModal({
  show, onHide, payment, onLinked,
}) {
  // --- Mode toggle ----------------------------------------------------
  //
  // 'manual' is the existing per-row picker (one row per booking, admin
  // types each booking_reference). 'auto' is the new
  // counterparty-FIFO mode that fetches outstanding lines for a partner
  // (or operator) and walks them top-down against the FIXED payment
  // amount — mirrors what BulkPaymentModal does for the Pay button.
  //
  // Default to MANUAL — the modal is opened in many places (single-row
  // 1:1 reconcile is still the most common case) and silently jumping
  // into auto mode would surprise admins who don't want it.
  const [mode, setMode] = useState(MODE_MANUAL);

  // --- Manual-mode (PaymentAllocationRows) state ----------------------
  // Stable per-row ids so React keys survive cross-row state changes
  // even when no booking has been picked yet.
  const rowIdCounter = useRef(0);
  const newRowId = () => {
    rowIdCounter.current += 1;
    return `row-${rowIdCounter.current}`;
  };

  const [rows, setRows] = useState(() => [
    newAllocationRow(newRowId(), ''),
  ]);

  // --- Auto-mode state ------------------------------------------------

  // Partner is the production case (Stripe → partner outstanding). The
  // operator option is here for symmetry but partner is the default.
  const [autoCounterpartyKind, setAutoCounterpartyKind] = useState('partner');
  const [autoCounterpartyId, setAutoCounterpartyId] = useState('');
  const [autoPeriodFrom, setAutoPeriodFrom] = useState(startOfMonthYmd());
  const [autoPeriodTo, setAutoPeriodTo] = useState(endOfMonthYmd());

  const [partners, setPartners] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const partnersRequestedRef = useRef(false);
  const operatorsRequestedRef = useRef(false);

  const [outstandingLines, setOutstandingLines] = useState([]);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [linesLoading, setLinesLoading] = useState(false);
  const [linesError, setLinesError] = useState('');
  // Per-line allocations keyed by `${booking_id}:${order_line_key}` —
  // same composite key BulkPaymentModal uses so two lines that share
  // the same order_line_key across DIFFERENT bookings each get their
  // own slot.
  const [allocations, setAllocations] = useState({});

  // --- Submit lifecycle ----------------------------------------------

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Reset on (re-)open so a fresh payment doesn't inherit stale state.
  // First manual row is pre-filled with the full payment amount — the
  // 1:1 reconciliation path is then zero extra clicks.
  useEffect(() => {
    if (show) {
      rowIdCounter.current = 0;
      setRows([{
        ...newAllocationRow(newRowId()),
        amount: formatRowAmount(payment?.gross_amount, payment?.currency),
      }]);
      setMode(MODE_MANUAL);
      setAutoCounterpartyKind('partner');
      setAutoCounterpartyId('');
      setAutoPeriodFrom(startOfMonthYmd());
      setAutoPeriodTo(endOfMonthYmd());
      setOutstandingLines([]);
      setTotalOutstanding(0);
      setTruncated(false);
      setLinesError('');
      setLinesLoading(false);
      setAllocations({});
      setSubmitError('');
      // Reset the lazy-load guards so a re-open refetches the lists.
      partnersRequestedRef.current = false;
      operatorsRequestedRef.current = false;
    }
  }, [show, payment?._id]);

  // --- Lazy-load partner / operator lists (auto mode) ----------------

  useEffect(() => {
    if (!show || mode !== MODE_AUTO) return undefined;
    if (autoCounterpartyKind !== 'partner') return undefined;
    if (partnersRequestedRef.current) return undefined;
    partnersRequestedRef.current = true;
    let cancelled = false;
    (async () => {
      setLoadingPartners(true);
      try {
        const response = await PartnersService.fetchPartners();
        if (cancelled) return;
        const list = response?.data?.data || [];
        setPartners(Array.isArray(list) ? list : []);
      } catch (err) {
        if (cancelled) return;
        setSubmitError(err.response?.data?.message || err.message || 'Failed to load partners');
        partnersRequestedRef.current = false;
      } finally {
        if (!cancelled) setLoadingPartners(false);
      }
    })();
    return () => { cancelled = true; };
  }, [show, mode, autoCounterpartyKind]);

  useEffect(() => {
    if (!show || mode !== MODE_AUTO) return undefined;
    if (autoCounterpartyKind !== 'operator') return undefined;
    if (operatorsRequestedRef.current) return undefined;
    operatorsRequestedRef.current = true;
    let cancelled = false;
    (async () => {
      setLoadingOperators(true);
      try {
        const response = await OperatorService.fetchOperators();
        if (cancelled) return;
        const list = response?.data?.data || [];
        setOperators(Array.isArray(list) ? list : []);
      } catch (err) {
        if (cancelled) return;
        setSubmitError(err.response?.data?.message || err.message || 'Failed to load operators');
        operatorsRequestedRef.current = false;
      } finally {
        if (!cancelled) setLoadingOperators(false);
      }
    })();
    return () => { cancelled = true; };
  }, [show, mode, autoCounterpartyKind]);

  // --- Outstanding-lines fetch (debounced, race-guarded) -------------
  //
  // Same pattern BulkPaymentModal uses: debounce by 250ms, sequence
  // number drops slower earlier responses on the floor.
  const fetchSeqRef = useRef(0);

  const currency = payment?.currency || 'EUR';

  const fetchOutstandingLines = useCallback(async () => {
    if (mode !== MODE_AUTO) return;
    if (!autoCounterpartyId || !autoCounterpartyKind
        || !autoPeriodFrom || !autoPeriodTo) {
      setOutstandingLines([]);
      setTotalOutstanding(0);
      setTruncated(false);
      setLinesError('');
      return;
    }
    const mySeq = fetchSeqRef.current + 1;
    fetchSeqRef.current = mySeq;
    setLinesLoading(true);
    setLinesError('');
    try {
      const data = await PaymentsService.listOutstandingLines({
        counterparty_kind: autoCounterpartyKind,
        counterparty_id: autoCounterpartyId,
        period_from: autoPeriodFrom,
        period_to: autoPeriodTo,
        // Inbound payment (the Stripe charge is money WE RECEIVED) — we
        // want the lines that ARE owed to us, i.e. the line_direction
        // 'in' from the customer/partner side. That matches what the
        // Pay-button flow does for partner settlements.
        line_direction: 'in',
        currency,
      });
      if (mySeq !== fetchSeqRef.current) return;
      const lines = Array.isArray(data?.lines) ? data.lines : [];
      setOutstandingLines(lines);
      setTotalOutstanding(Number(data?.total_outstanding) || 0);
      setTruncated(!!data?.truncated);
      // Reset allocations on a fresh fetch — old keys may no longer
      // exist and stale rows would skew the running total.
      setAllocations({});
    } catch (err) {
      if (mySeq !== fetchSeqRef.current) return;
      setLinesError(
        err.response?.data?.message || err.message || 'Failed to load outstanding lines',
      );
      setOutstandingLines([]);
      setTotalOutstanding(0);
      setTruncated(false);
    } finally {
      if (mySeq === fetchSeqRef.current) setLinesLoading(false);
    }
  }, [mode, autoCounterpartyKind, autoCounterpartyId, autoPeriodFrom, autoPeriodTo, currency]);

  useEffect(() => {
    if (!show) return undefined;
    const handle = setTimeout(() => { fetchOutstandingLines(); }, 250);
    return () => clearTimeout(handle);
  }, [show, fetchOutstandingLines]);

  // --- Derived totals (auto mode) ------------------------------------

  const expected = Number(payment?.gross_amount ?? 0);

  const allocationsTotal = useMemo(() => round2(
    Object.values(allocations).reduce((sum, v) => {
      const n = Number(v);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0),
  ), [allocations]);

  const amountValid = Number.isFinite(expected) && expected > 0;
  const allocationsMatch = amountValid && Math.abs(allocationsTotal - expected) < 0.005;
  const stillOutstandingAfter = round2(totalOutstanding - allocationsTotal);
  const overflowAmount = round2(expected - totalOutstanding);
  const hasOverflow = amountValid && overflowAmount > 0.005;

  // --- Auto-allocate FIFO --------------------------------------------

  const allocKey = useCallback(
    (line) => `${line?.booking_id ?? ''}:${line?.order_line_key ?? ''}`,
    [],
  );

  const autoAllocateTopDown = useCallback(() => {
    if (!amountValid) {
      setAllocations({});
      return;
    }
    let remaining = expected;
    const next = {};
    outstandingLines.forEach((line) => {
      if (remaining <= 0.005) return;
      const lineOutstanding = Number(line.outstanding_amount) || 0;
      if (lineOutstanding <= 0.005) return;
      // Skip lines whose own currency doesn't match the payment
      // currency — they're surfaced for visibility but can't be
      // allocated until the admin changes the payment currency (which,
      // for an unmatched Stripe payment, is locked).
      if (line.currency && line.currency !== currency) return;
      const allocate = Math.min(remaining, lineOutstanding);
      next[allocKey(line)] = round2(allocate);
      remaining = round2(remaining - allocate);
    });
    setAllocations(next);
  }, [amountValid, expected, outstandingLines, allocKey, currency]);

  // Auto-allocate whenever the lines change. Manual per-row edits
  // afterwards override the row's slot without re-running this — we
  // DON'T add `allocations` to the dep list (infinite loop + would
  // stomp manual edits).
  useEffect(() => {
    if (!show || mode !== MODE_AUTO) return;
    autoAllocateTopDown();
  }, [show, mode, autoAllocateTopDown]);

  // --- Manual-mode validation (unchanged path) ----------------------

  const manualValidation = validateAllocationRows(rows, expected);
  const canSubmitManual = manualValidation.canSubmit && !submitting;

  // --- Auto-mode validation ------------------------------------------

  const allocationValid = allocationsMatch
    && Object.values(allocations).every((v) => {
      const n = Number(v);
      return Number.isFinite(n) && n >= 0;
    });
  const canSubmitAuto = !!autoCounterpartyId
    && outstandingLines.length > 0
    && allocationValid
    && !submitting;

  const canSubmit = mode === MODE_AUTO ? canSubmitAuto : canSubmitManual;

  // --- Auto-mode handlers --------------------------------------------

  const handleAutoKindChange = (nextKind) => {
    setAutoCounterpartyKind(nextKind);
    setAutoCounterpartyId('');
    setAllocations({});
  };

  const handlePerRowAllocate = (key, value) => {
    setAllocations((prev) => ({ ...prev, [key]: value }));
  };

  const handleRowMax = (line) => {
    const key = allocKey(line);
    const lineOutstanding = Number(line.outstanding_amount) || 0;
    const currentRowValue = Number(allocations[key]) || 0;
    const otherAllocations = allocationsTotal - currentRowValue;
    const remaining = Math.max(0, expected - otherAllocations);
    const next = Math.min(remaining, lineOutstanding);
    handlePerRowAllocate(key, round2(next));
  };

  // --- Submit ---------------------------------------------------------

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!payment || !canSubmit) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      if (mode === MODE_AUTO) {
        // Build lines[] from non-zero per-line allocations. The first
        // line's booking_id seeds the payment.counterparty.id — the
        // PaymentReassign schema requires counterparty.kind='booking'
        // on multi-booking splits, and the service routes attribution
        // through lines[] regardless of which booking is the "primary".
        const linesPayload = outstandingLines
          .map((line) => {
            const amt = Number(allocations[allocKey(line)]);
            if (!Number.isFinite(amt) || amt <= 0) return null;
            const entry = {
              booking_id: line.booking_id,
              gross_amount: amt,
            };
            if (line.order_line_key) entry.order_line_key = line.order_line_key;
            return entry;
          })
          .filter(Boolean);
        if (linesPayload.length === 0) {
          setSubmitError('Enter at least one non-zero allocation.');
          setSubmitting(false);
          return;
        }
        const updated = await PaymentsService.reassignPayment(
          payment._id,
          { kind: 'booking', id: linesPayload[0].booking_id },
          linesPayload,
        );
        if (onLinked) onLinked(updated);
        return;
      }

      // Manual mode — unchanged path.
      const counterpartyId = rows[0].matchedBooking._id;
      const anyHasLine = rows.some((r) => r.orderLineKey);
      const isMulti = rows.length >= 2;
      const useLinesShape = isMulti || anyHasLine;
      const lines = useLinesShape ? rowsToLinesPayload(rows) : undefined;
      const updated = await PaymentsService.reassignPayment(
        payment._id,
        { kind: 'booking', id: counterpartyId },
        lines,
      );
      if (onLinked) onLinked(updated);
    } catch (err) {
      setSubmitError(err.response?.data?.message || err.message || 'Failed to link payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (submitting) return;
    if (onHide) onHide();
  };

  // --- Render helpers (auto mode) ------------------------------------

  const renderAutoCounterpartyPicker = () => {
    if (autoCounterpartyKind === 'partner') {
      return (
        <Form.Select
          value={autoCounterpartyId}
          onChange={(e) => setAutoCounterpartyId(e.target.value)}
          disabled={loadingPartners}
          aria-label="Partner"
          data-testid="ltb-auto-partner"
        >
          <option value="">
            {loadingPartners ? 'Loading partners…' : 'Select partner…'}
          </option>
          {partners.map((p) => {
            const id = counterpartyIdOf(p);
            return (
              <option key={id} value={id}>
                {p.name || id}
              </option>
            );
          })}
        </Form.Select>
      );
    }
    return (
      <Form.Select
        value={autoCounterpartyId}
        onChange={(e) => setAutoCounterpartyId(e.target.value)}
        disabled={loadingOperators}
        aria-label="Operator"
        data-testid="ltb-auto-operator"
      >
        <option value="">
          {loadingOperators ? 'Loading operators…' : 'Select operator…'}
        </option>
        {operators.map((o) => {
          const id = counterpartyIdOf(o);
          return (
            <option key={id} value={id}>
              {o.name || id}
            </option>
          );
        })}
      </Form.Select>
    );
  };

  const renderAutoLinesTable = () => {
    if (!autoCounterpartyId) {
      return (
        <div className="small text-muted" data-testid="ltb-auto-pick-counterparty">
          Pick a counterparty above to load outstanding lines.
        </div>
      );
    }
    if (linesLoading) {
      return (
        <div className="d-flex align-items-center small text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading outstanding lines…
        </div>
      );
    }
    if (linesError) {
      return <div className="alert alert-warning py-2 mb-0 small">{linesError}</div>;
    }
    if (outstandingLines.length === 0) {
      return (
        <div className="alert alert-secondary py-2 mb-0 small" role="status">
          No outstanding lines for this counterparty in this period.
        </div>
      );
    }
    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="small text-muted">
            {outstandingLines.length}
            {' '}
            outstanding line
            {outstandingLines.length === 1 ? '' : 's'}
            {' '}
            — sorted oldest first
          </div>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={autoAllocateTopDown}
            disabled={!amountValid}
            data-testid="ltb-auto-allocate-btn"
          >
            <i className="fa fa-arrow-down me-1" />
            Auto-allocate top-down
          </Button>
        </div>
        {truncated && (
          <div className="alert alert-warning py-2 mb-2 small" role="alert">
            Showing first
            {' '}
            {outstandingLines.length}
            {' '}
            outstanding lines — narrow your period to see more.
          </div>
        )}
        <Table size="sm" responsive className="mb-2" data-testid="ltb-auto-lines-table">
          <thead>
            <tr>
              <th>Booking ref</th>
              <th>Date</th>
              <th>Description</th>
              <th className="text-end">Owed</th>
              <th className="text-end">Paid so far</th>
              <th className="text-end">Outstanding</th>
              <th style={{ width: 180 }} className="text-end">Allocate now</th>
            </tr>
          </thead>
          <tbody>
            {outstandingLines.map((line, idx) => {
              const key = allocKey(line);
              const val = allocations[key];
              const valNum = Number(val);
              const lineOutstanding = Number(line.outstanding_amount) || 0;
              const overAllocated = Number.isFinite(valNum) && valNum > lineOutstanding + 0.005;
              const currencyMismatch = !!line.currency && line.currency !== currency;
              const allocateControl = (
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  size="sm"
                  value={val ?? ''}
                  isInvalid={overAllocated}
                  disabled={currencyMismatch}
                  onChange={(ev) => handlePerRowAllocate(key, ev.target.value)}
                  aria-label={`Line ${idx + 1} allocation`}
                />
              );
              return (
                <tr key={key}>
                  <td>
                    <code className="small" title={line.booking_id}>
                      {line.booking_reference || line.booking_id}
                    </code>
                  </td>
                  <td className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                    {line.date ? formatDate(line.date) : '—'}
                  </td>
                  <td className="small">
                    {line.description || line.kind || '—'}
                    {line.full_name && (
                      <div className="text-muted" style={{ fontSize: '0.8em' }}>
                        {line.full_name}
                      </div>
                    )}
                  </td>
                  <td className="text-end small">
                    {formatMoney(line.owed_amount, line.currency || currency)}
                  </td>
                  <td className="text-end small text-muted">
                    {formatMoney(line.paid_amount, line.currency || currency)}
                  </td>
                  <td className="text-end small fw-bold">
                    {formatMoney(line.outstanding_amount, line.currency || currency)}
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      {currencyMismatch ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={(
                            <Tooltip>
                              {`Change payment currency to ${line.currency} to allocate this line.`}
                            </Tooltip>
                          )}
                        >
                          <span className="d-inline-block">{allocateControl}</span>
                        </OverlayTrigger>
                      ) : allocateControl}
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => handleRowMax(line)}
                        disabled={!amountValid || currencyMismatch}
                        aria-label={`Line ${idx + 1} max`}
                        title={currencyMismatch
                          ? `Change payment currency to ${line.currency} to allocate this line.`
                          : 'Fill with min(remaining unallocated, line outstanding)'}
                      >
                        max
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <div className="d-flex flex-wrap gap-3 justify-content-end small">
          <div className={allocationsMatch ? 'text-success' : 'text-danger'} data-testid="ltb-auto-summary">
            Allocated:
            {' '}
            <strong>{formatMoney(allocationsTotal, currency)}</strong>
            {' '}
            /
            {' '}
            {formatMoney(expected, currency)}
          </div>
          <div className="text-muted">
            Still outstanding after this payment:
            {' '}
            <strong>{formatMoney(stillOutstandingAfter, currency)}</strong>
          </div>
        </div>
        {hasOverflow && (
          <div className="alert alert-warning py-2 mt-2 mb-0 small" role="alert">
            Payment amount exceeds total outstanding by
            {' '}
            <strong>{formatMoney(overflowAmount, currency)}</strong>
            {' '}
            — pick a wider period or expect unallocated overflow.
          </div>
        )}
      </>
    );
  };

  return (
    <Modal show={show} onHide={handleCancel} size={mode === MODE_AUTO ? 'xl' : 'lg'}>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-link me-2" />
          Link Payment to Booking
          {mode === MODE_MANUAL && rows.length > 1 ? 's' : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {payment && (
          <div className="mb-3 p-3 bg-light rounded">
            <div className="text-muted small mb-1">Unmatched Stripe payment</div>
            <div className="fw-bold">
              {formatMoney(payment.gross_amount, payment.currency)}
              {' '}
              <span className="text-muted small">
                (
                {payment.currency || '—'}
                )
              </span>
            </div>
            <div className="small text-muted">
              {payment.occurred_at ? formatDateTime(payment.occurred_at) : '—'}
            </div>
            {payment.external_refs?.stripe_charge_id && (
              <div className="small mt-1">
                <code>{payment.external_refs.stripe_charge_id}</code>
              </div>
            )}
            {/* Who paid. This is where the matching decision is actually
                made, so the payer's name and address belong here and not
                only in the detail view. */}
            <PayerBillingDetails payment={payment} className="mt-2 pt-2 border-top" />
          </div>
        )}

        {/* Mode toggle — the manual flow is the existing per-row
            picker, the auto flow is FIFO across a counterparty's
            outstanding lines (same shape BulkPaymentModal uses on the
            Pay button). Toggle is a radio pair so the active mode is
            always visible. */}
        <Form.Group className="mb-3">
          <Form.Label className="d-block small text-muted">Allocation mode</Form.Label>
          <Form.Check
            inline
            type="radio"
            id="ltb_mode_manual"
            name="ltb_mode"
            label="Manual rows (pick bookings)"
            checked={mode === MODE_MANUAL}
            onChange={() => setMode(MODE_MANUAL)}
            disabled={submitting}
            data-testid="ltb-mode-manual"
          />
          <Form.Check
            inline
            type="radio"
            id="ltb_mode_auto"
            name="ltb_mode"
            label="Auto-allocate to partner (FIFO)"
            checked={mode === MODE_AUTO}
            onChange={() => setMode(MODE_AUTO)}
            disabled={submitting}
            data-testid="ltb-mode-auto"
          />
        </Form.Group>

        <Error error={submitError} setError={setSubmitError} />

        {mode === MODE_MANUAL ? (
          <Form onSubmit={handleSubmit}>
            <PaymentAllocationRows
              rows={rows}
              onChange={setRows}
              expectedTotal={expected}
              currency={currency}
              sideMode="customer"
              disabled={submitting}
              testIdPrefix="ltb"
              matchSignals={{
                amount: payment?.gross_amount,
                currency: payment?.currency,
                date: payment?.occurred_at,
                email: payment?.payer_email,
                // Absent when the API judged the payer-email join ambiguous,
                // so an ambiguous join never becomes a scoring signal.
                name: payment?.payer_billing?.billing_details?.name,
              }}
            />
          </Form>
        ) : (
          <Form onSubmit={handleSubmit}>
            {/* A) Counterparty + period */}
            <Card className="mb-3">
              <Card.Body>
                <Card.Title as="h6" className="mb-3">Who & when</Card.Title>

                <Form.Group className="mb-3">
                  <Form.Label className="d-block">Counterparty kind</Form.Label>
                  <Form.Check
                    inline
                    type="radio"
                    id="ltb_auto_kind_partner"
                    name="ltb_auto_kind"
                    label="Partner"
                    checked={autoCounterpartyKind === 'partner'}
                    onChange={() => handleAutoKindChange('partner')}
                    disabled={submitting}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="ltb_auto_kind_operator"
                    name="ltb_auto_kind"
                    label="Operator"
                    checked={autoCounterpartyKind === 'operator'}
                    onChange={() => handleAutoKindChange('operator')}
                    disabled={submitting}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    {autoCounterpartyKind === 'operator' ? 'Operator' : 'Partner'}
                    {' *'}
                  </Form.Label>
                  {renderAutoCounterpartyPicker()}
                </Form.Group>

                <div className="row g-2">
                  <Form.Group className="col-md-6" controlId="ltb_auto_period_from">
                    <Form.Label>From *</Form.Label>
                    <Form.Control
                      type="date"
                      value={autoPeriodFrom}
                      onChange={(e) => setAutoPeriodFrom(e.target.value)}
                      disabled={submitting}
                    />
                  </Form.Group>
                  <Form.Group className="col-md-6" controlId="ltb_auto_period_to">
                    <Form.Label>To *</Form.Label>
                    <Form.Control
                      type="date"
                      value={autoPeriodTo}
                      onChange={(e) => setAutoPeriodTo(e.target.value)}
                      disabled={submitting}
                    />
                  </Form.Group>
                </div>
              </Card.Body>
            </Card>

            {/* B) Outstanding lines */}
            <Card className="mb-3">
              <Card.Body>
                <Card.Title as="h6" className="mb-3">Outstanding lines</Card.Title>
                {renderAutoLinesTable()}
              </Card.Body>
            </Card>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {(() => {
            if (submitting) return 'Linking…';
            if (mode === MODE_AUTO) {
              const n = Object.values(allocations)
                .filter((v) => Number(v) > 0).length;
              return `Link Payment${n > 1 ? ` (${n} allocations)` : ''}`;
            }
            return `Link Payment${rows.length > 1 ? ` (${rows.length} allocations)` : ''}`;
          })()}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default LinkToBookingModal;
